import React, { useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { GlobalContext } from '../context/GlobalContext';
import { Icons } from '../design/Icons';
import {
  SECTIONS, SECTIONS_BOTTOM, resolveRoute, canAccess,
} from './navConfig';
import { useNotificationsUnread } from '../hooks/useNotificationsUnread';

// The left rail: fixed-width icon-only navigation (56px).
// Uses monoline icons; the active item gets an orange accent stripe.

const RailItem = ({ item, active, onClick, badge = 0 }) => {
  const IconEl = Icons[item.icon] || Icons.dots;
  return (
    <button
      className={`rail-item ${active ? 'active' : ''}`}
      onClick={onClick}
      title={badge > 0 ? `${item.label} (${badge} unread)` : item.label}
      aria-label={item.label}
      style={{ position: 'relative' }}
    >
      <IconEl size={18} />
      {badge > 0 && (
        // The rail is a narrow icon-only column — a numeric badge in this
        // space reads as noise. Use a small red pulsing dot as an "attention"
        // marker instead; the actual count still shows on the top bar bell
        // and on the /notifications page.
        <span
          aria-label={`${badge} unread`}
          style={{
            position: 'absolute',
            top: 8, left: 10,
            width: 8, height: 8,
            borderRadius: '50%',
            background: 'var(--stop)',
            boxShadow: '0 0 0 2px var(--bg)',
            pointerEvents: 'none',
            animation: 'pulse 1.8s infinite',
          }}
        />
      )}
      <span className="rail-tip">{item.label}</span>
    </button>
  );
};

const Rail = () => {
  const nav = useNavigate();
  const { pathname } = useLocation();
  const { uinfo } = useContext(GlobalContext);
  const { section: activeKey } = resolveRoute(pathname);
  const status = uinfo?.Status;
  const unreadNotifs = useNotificationsUnread();

  const go = (base) => () => nav(base);

  const visible = (list) => list.filter(s => canAccess(s.roles, status));

  // Per-item badge count — only Notifications carries one today. If we add
  // more (e.g. chat), extend this map rather than special-casing in JSX.
  const badgeFor = (key) => (key === 'notif' ? unreadNotifs : 0);

  return (
    <nav className="rail">
      <div className="rail-logo">
        <img src="/automotivemation-mark.png" alt="Sales Assist" width="30" height="30"/>
      </div>
      <div className="rail-stack">
        {visible(SECTIONS).map(it => (
          <RailItem key={it.key} item={it} active={activeKey === it.key} onClick={go(it.base)} badge={badgeFor(it.key)}/>
        ))}
      </div>
      <div className="rail-stack bottom">
        {visible(SECTIONS_BOTTOM).map(it => (
          <RailItem key={it.key} item={it} active={activeKey === it.key} onClick={go(it.base)} badge={badgeFor(it.key)}/>
        ))}
      </div>
    </nav>
  );
};

export default Rail;
